/* /assets/codelia-banner-carousel.js
   Codelia Banner Carousel (sections/banner-carousel-codelia.liquid).
   Self-contained like codelia-slideshow.js: reuses Flickity from the Pipeline vendor
   bundle (themeVendor.Flickity) without registering into theme.js's section
   framework, so it survives theme updates.

   Flickity only moves the track. The StyleKorean-style sizing -- centre card enlarged
   on desktop, neighbours pushed out so gaps stay equal -- is CSS keyed off the
   is-active / is-before / is-after classes this script puts on each card. */

(function () {
  'use strict';

  // The section outputs one script tag per instance — only boot once.
  if (window.codeliaBannerCarouselLoaded) return;
  window.codeliaBannerCarouselLoaded = true;

  var selectors = {
    section: '[data-section-type="codelia-banner-carousel"]',
    stage: '.banner-carousel__stage',
    track: '[data-banner-carousel]',
    slide: '.banner-carousel__slide',
    card: '.banner-carousel__card',
    prev: '[data-banner-prev]',
    next: '[data-banner-next]',
    pause: '[data-banner-pause]',
    current: '[data-banner-current]',
    progress: '[data-banner-progress]',
    focusable: 'a, button, input, select, textarea, [tabindex]'
  };

  var classes = {
    active: 'is-active',
    before: 'is-before',
    after: 'is-after',
    noTransition: 'banner-carousel--no-transition',
    enabled: 'flickity-enabled'
  };

  var DESKTOP_QUERY = '(min-width: 768px)';
  var DRAG_SLOP = 8;

  var instances = {};

  function BannerCarousel(container) {
    this.container = container;
    this.stage = container.querySelector(selectors.stage);
    this.track = container.querySelector(selectors.track);
    this.flkty = null;

    if (!this.track) return;

    // The merchant's banners. this.slides/this.cards also include the loop clones.
    this.originals = Array.prototype.slice.call(this.track.querySelectorAll(selectors.slide));
    this.count = this.originals.length;
    this.refreshCells();
    this.speed = parseInt(this.track.getAttribute('data-speed'), 10) || 3000;
    this.autoplay = this.track.getAttribute('data-autoplay') === 'true' && this.count > 1;
    this.hoverPause = this.track.getAttribute('data-hover-pause') === 'true';
    this.hoverCapable = window.matchMedia('(hover: hover)').matches;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Everything that can hold autoplay back. Autoplay runs only when all are false.
    this.userPaused = this.reducedMotion;
    this.isHovered = false;
    this.isOutOfView = false;
    this.editorHold = false;

    this.pauseButtons = Array.prototype.slice.call(container.querySelectorAll(selectors.pause));
    this.currentEls = Array.prototype.slice.call(container.querySelectorAll(selectors.current));
    this.progressBar = container.querySelector(selectors.progress);

    this.init();
  }

  BannerCarousel.prototype.init = function () {
    var Flickity = window.themeVendor && window.themeVendor.Flickity;
    if (!Flickity) return;

    // Clone before Flickity measures, so it starts with a track wide enough to loop.
    this.makeClones().forEach(function (clone) {
      this.track.appendChild(clone);
    }, this);
    this.refreshCells();

    this.flkty = new Flickity(this.track, {
      cellSelector: selectors.slide,
      cellAlign: 'center',
      // The loop clones guarantee the track is always wide enough, so wrapping never
      // has to switch itself off ('fill' would, rather than leave gaps).
      wrapAround: 'fill',
      contain: false,
      percentPosition: false,
      pageDots: false,
      prevNextButtons: false,
      draggable: this.count > 1,
      autoPlay: this.autoplay ? this.speed : false,
      // Hover pause is handled on the stage below, so it also covers the mobile UI.
      pauseAutoPlayOnHover: false,
      // Flickity's default physics (selectedAttraction 0.025, friction 0.28) settle in
      // ~750ms at 60fps, landing with the 0.75s card resize -- so they are left as is.
      accessibility: true,
      rightToLeft: window.isRTL
    });

    this.onSelect = this.handleSelect.bind(this);
    this.flkty.on('select', this.onSelect);

    // A wider window (or the switch to wider desktop cards) can need more clones.
    this.onFlktyResize = this.topUpClones.bind(this);
    this.flkty.on('resize', this.onFlktyResize);

    // Flickity stops its player for good on any manual interaction. Restart it
    // instead, so manual navigation only resets the interval.
    var restart = this.restartPlayer.bind(this);
    this.flkty.on('dragEnd', restart);
    this.flkty.on('staticClick', restart);
    this.flkty.on('uiChange', restart);

    this.bindControls();
    this.bindTapToSelect();
    this.bindHover();
    this.bindVisibility();
    this.bindBreakpoint();
    this.bindPageVisibility();

    this.handleSelect();
    this.updatePauseButtons();
    this.syncPlayer();
  };

  // == Endless loop ==
  // Flickity only wraps (next on the last banner carries on to the first) while the
  // banners are wider than about two screens; with fewer or narrower banners it
  // turns wrapping off and rewinds instead. Like Swiper's loop mode on StyleKorean,
  // whole copies of the banner set are appended until the track is wide enough, so
  // the carousel always runs on endlessly in both directions.
  BannerCarousel.prototype.refreshCells = function () {
    this.slides = this.flkty
      ? this.flkty.getCellElements()
      : Array.prototype.slice.call(this.track.querySelectorAll(selectors.slide));
    this.cards = this.slides.map(function (slide) {
      return slide.querySelector(selectors.card);
    });
  };

  BannerCarousel.prototype.makeClones = function () {
    if (this.count < 2) return [];
    var first = this.originals[0];
    var pitch = first.offsetWidth + (parseFloat(window.getComputedStyle(first).marginRight) || 0);
    var width = this.track.clientWidth;
    if (!pitch || !width) return [];

    // Two track-widths of cards plus one on each side keeps both edges covered while
    // a slide is moving and the centre card is enlarged.
    var needed = Math.ceil((2 * width) / pitch) + 2;
    var sets = Math.ceil((needed - this.slides.length) / this.count);
    var clones = [];
    for (var s = 0; s < sets; s++) {
      this.originals.forEach(function (slide) {
        clones.push(this.cloneSlide(slide));
      }, this);
    }
    return clones;
  };

  BannerCarousel.prototype.cloneSlide = function (slide) {
    var clone = slide.cloneNode(true);
    clone.setAttribute('data-banner-clone', '');
    // Clones are not editor blocks: the theme editor must only highlight the original.
    Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
      if (attr.name.indexOf('data-shopify-editor') === 0) clone.removeAttribute(attr.name);
    });
    clone.querySelectorAll('[id]').forEach(function (el) {
      el.removeAttribute('id');
    });
    clone.querySelectorAll('img').forEach(function (img) {
      img.removeAttribute('fetchpriority');
      img.setAttribute('loading', 'lazy');
    });
    return clone;
  };

  BannerCarousel.prototype.topUpClones = function () {
    if (!this.flkty) return;
    var clones = this.makeClones();
    if (!clones.length) return;
    this.flkty.append(clones);
    this.refreshCells();
    this.handleSelect();
  };

  // The merchant's banner (0-based) that a cell shows, clones included.
  BannerCarousel.prototype.bannerIndex = function (cellIndex) {
    var slide = this.slides[cellIndex];
    return slide ? parseInt(slide.getAttribute('data-slide-index'), 10) || 0 : 0;
  };

  // == Card classes ==
  // Flickity's own is-selected cannot say which cards sit to the left or right on
  // screen: with wrap-around the last cell in the DOM is drawn to the left of the
  // first. So work out each card's shortest distance from the selected one instead.
  BannerCarousel.prototype.handleSelect = function () {
    if (!this.flkty) return;
    var selected = this.flkty.selectedIndex;
    var count = this.slides.length;
    var wrapping = this.flkty.isWrapping;

    this.cards.forEach(function (card, index) {
      if (!card) return;
      var distance = index - selected;
      if (wrapping) {
        distance = ((distance % count) + count) % count;
        if (distance > count / 2) distance -= count;
      }
      card.classList.toggle(classes.active, distance === 0);
      card.classList.toggle(classes.before, distance < 0);
      card.classList.toggle(classes.after, distance > 0);
    });

    // Only the centred card's links are reachable by keyboard.
    this.slides.forEach(function (slide, index) {
      var isCurrent = index === selected;
      slide.querySelectorAll(selectors.focusable).forEach(function (el) {
        if (isCurrent) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', '-1');
        }
      });
    });

    // Counter and progress bar always refer to the merchant's banners, not clones.
    var position = this.bannerIndex(selected) + 1;
    this.currentEls.forEach(function (el) {
      el.textContent = position;
    });
    if (this.progressBar) {
      this.progressBar.style.setProperty('--bc-progress', position / this.count);
    }
  };

  // == Autoplay ==
  BannerCarousel.prototype.shouldPlay = function () {
    return this.autoplay && !this.userPaused && !this.isHovered && !this.isOutOfView && !this.editorHold;
  };

  BannerCarousel.prototype.syncPlayer = function () {
    if (!this.flkty || !this.autoplay) return;
    // Stop rather than pause: a paused Flickity player resumes by itself when the
    // browser tab becomes visible again, which would override the visitor's pause.
    if (this.shouldPlay()) {
      this.flkty.playPlayer();
    } else {
      this.flkty.stopPlayer();
    }
  };

  // Manual navigation restarts the interval from zero, so the next automatic change
  // always comes a full interval after the visitor's action.
  BannerCarousel.prototype.restartPlayer = function () {
    if (!this.flkty || !this.autoplay) return;
    this.flkty.stopPlayer();
    this.syncPlayer();
  };

  BannerCarousel.prototype.togglePause = function () {
    this.userPaused = !this.userPaused;
    this.updatePauseButtons();
    this.restartPlayer();
  };

  // WAI-ARIA carousel pattern: the button's label names the action it will take
  // ("Pause slideshow" / "Play slideshow") rather than using aria-pressed.
  BannerCarousel.prototype.updatePauseButtons = function () {
    var paused = this.userPaused;
    this.pauseButtons.forEach(function (btn) {
      btn.setAttribute('data-paused', paused ? 'true' : 'false');
      btn.setAttribute('aria-label', btn.getAttribute(paused ? 'data-play-label' : 'data-pause-label'));
    });
  };

  // == Controls ==
  BannerCarousel.prototype.bindControls = function () {
    var self = this;
    this.container.querySelectorAll(selectors.prev).forEach(function (el) {
      el.addEventListener('click', function () {
        self.flkty.previous(true);
        self.restartPlayer();
      });
    });
    this.container.querySelectorAll(selectors.next).forEach(function (el) {
      el.addEventListener('click', function () {
        self.flkty.next(true);
        self.restartPlayer();
      });
    });
    this.pauseButtons.forEach(function (el) {
      el.addEventListener('click', self.togglePause.bind(self));
    });
  };

  // A tap on a card that is not centred brings it to the centre instead of
  // following its link. Listens in the capture phase so the link's default action
  // can still be cancelled; anything that travelled more than a few pixels is a
  // swipe, not a tap. Same approach as codelia-slideshow.js's peek mode.
  BannerCarousel.prototype.bindTapToSelect = function () {
    var self = this;
    var downX = 0;
    var downY = 0;

    this.onPointerDown = function (evt) {
      downX = evt.clientX;
      downY = evt.clientY;
    };

    this.onClickCapture = function (evt) {
      if (Math.abs(evt.clientX - downX) > DRAG_SLOP || Math.abs(evt.clientY - downY) > DRAG_SLOP) return;
      var slide = evt.target.closest(selectors.slide);
      if (!slide || !self.track.contains(slide)) return;
      var index = self.slides.indexOf(slide);
      if (index < 0 || index === self.flkty.selectedIndex) return;
      evt.preventDefault();
      evt.stopPropagation();
      self.flkty.select(index);
      self.restartPlayer();
    };

    this.track.addEventListener('pointerdown', this.onPointerDown, true);
    this.track.addEventListener('click', this.onClickCapture, true);
  };

  BannerCarousel.prototype.bindHover = function () {
    if (!this.hoverPause || !this.hoverCapable || !this.stage) return;
    var self = this;
    this.onMouseEnter = function () {
      self.isHovered = true;
      self.syncPlayer();
    };
    this.onMouseLeave = function () {
      self.isHovered = false;
      self.syncPlayer();
    };
    this.stage.addEventListener('mouseenter', this.onMouseEnter);
    this.stage.addEventListener('mouseleave', this.onMouseLeave);
  };

  // Hold autoplay while the carousel is scrolled out of view.
  BannerCarousel.prototype.bindVisibility = function () {
    if (!('IntersectionObserver' in window)) return;
    var self = this;
    this.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.isOutOfView = !entry.isIntersecting;
        self.syncPlayer();
      });
    });
    this.observer.observe(this.container);
  };

  // Flickity defers play() until a hidden browser tab becomes visible, and then starts
  // regardless of anything that happened meanwhile (e.g. the visitor pressing pause).
  // Re-apply our own state just after Flickity's handler has run.
  BannerCarousel.prototype.bindPageVisibility = function () {
    var self = this;
    this.onPageVisibility = function () {
      if (document.hidden) return;
      window.setTimeout(function () {
        self.syncPlayer();
      }, 0);
    };
    document.addEventListener('visibilitychange', this.onPageVisibility);
  };

  // Flickity re-measures on window resize by itself. Crossing the breakpoint changes
  // the card sizes, so cards snap to their new size rather than animating there.
  BannerCarousel.prototype.bindBreakpoint = function () {
    var self = this;
    this.mql = window.matchMedia(DESKTOP_QUERY);
    this.onBreakpoint = function () {
      // Flickity ignores resize() while a slide is still moving (an iOS 15
      // workaround), so a breakpoint crossed mid-transition -- e.g. rotating a
      // tablet -- would otherwise keep the old layout. Wait for it to settle.
      if (!self.flkty || !self.flkty.isActive) return;
      if (self.flkty.isAnimating) {
        self.flkty.once('settle', self.onBreakpoint);
        return;
      }
      self.container.classList.add(classes.noTransition);
      self.flkty.resize();
      self.handleSelect();
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          self.container.classList.remove(classes.noTransition);
        });
      });
    };
    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onBreakpoint);
    } else if (this.mql.addListener) {
      this.mql.addListener(this.onBreakpoint);
    }
  };

  // == Theme editor ==
  BannerCarousel.prototype.onBlockSelect = function (evt) {
    if (!this.flkty) return;
    var slide = evt.target.closest(selectors.slide);
    if (!slide || this.slides.indexOf(slide) < 0) return;
    // Show the nearest copy of that banner rather than rewinding to the original.
    var banner = parseInt(slide.getAttribute('data-slide-index'), 10) || 0;
    var selected = this.flkty.selectedIndex;
    var total = this.slides.length;
    var index = -1;
    var best = Infinity;
    for (var i = 0; i < total; i++) {
      if (this.bannerIndex(i) !== banner) continue;
      var d = Math.abs(i - selected);
      d = Math.min(d, total - d);
      if (d < best) {
        best = d;
        index = i;
      }
    }
    if (index < 0) return;
    this.editorHold = true;
    this.flkty.select(index, false, true);
    this.syncPlayer();
  };

  BannerCarousel.prototype.onBlockDeselect = function () {
    this.editorHold = false;
    this.restartPlayer();
  };

  BannerCarousel.prototype.unload = function () {
    if (this.observer) this.observer.disconnect();
    if (this.onPageVisibility) document.removeEventListener('visibilitychange', this.onPageVisibility);
    if (this.mql && this.onBreakpoint) {
      if (this.mql.removeEventListener) {
        this.mql.removeEventListener('change', this.onBreakpoint);
      } else if (this.mql.removeListener) {
        this.mql.removeListener(this.onBreakpoint);
      }
    }
    if (this.stage && this.onMouseEnter) {
      this.stage.removeEventListener('mouseenter', this.onMouseEnter);
      this.stage.removeEventListener('mouseleave', this.onMouseLeave);
    }
    if (this.track && this.onClickCapture) {
      this.track.removeEventListener('pointerdown', this.onPointerDown, true);
      this.track.removeEventListener('click', this.onClickCapture, true);
    }
    if (this.flkty && this.track.classList.contains(classes.enabled)) {
      this.flkty.destroy();
    }
  };

  function initSections(root) {
    (root || document).querySelectorAll(selectors.section).forEach(function (container) {
      var id = container.getAttribute('data-section-id');
      if (instances[id]) return;
      instances[id] = new BannerCarousel(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initSections();
    });
  } else {
    initSections();
  }

  // Shopify theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (evt) {
    var container = evt.target.querySelector(selectors.section);
    if (!container) return;
    var id = container.getAttribute('data-section-id');
    if (instances[id]) {
      instances[id].unload();
      delete instances[id];
    }
    instances[id] = new BannerCarousel(container);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var instance = instances[evt.detail.sectionId];
    if (!instance) return;
    instance.unload();
    delete instances[evt.detail.sectionId];
  });

  document.addEventListener('shopify:block:select', function (evt) {
    var instance = instances[evt.detail.sectionId];
    if (instance) instance.onBlockSelect(evt);
  });

  document.addEventListener('shopify:block:deselect', function (evt) {
    var instance = instances[evt.detail.sectionId];
    if (instance) instance.onBlockDeselect();
  });
})();
